/**
 * DoorwayMotifs — hand-crafted SVG art for the six OML doorways.
 * ─────────────────────────────────────────────────────────────────
 * Feb 27 2026 · Michael picked "Option A · Signature Motif" from
 * /preview-lab. Each doorway gets a unique, on-brand SVG scene
 * that reads instantly and animates gently. Zero image dependency,
 * scales infinitely, no storage cost.
 *
 * Palette: navy field, gold arcs (#e0a97a), cream focal (#f5ecd9),
 * with each doorway tinted by a signature accent so the six cards
 * feel like a family but distinguishable at a glance.
 *
 * All motifs share the same 400×260 viewBox and preserveAspectRatio
 * so they slot into the 16:10 preview tile identically.
 * ────────────────────────────────────────────────────────────── */
import React from "react";

const GOLD = "#e0a97a";
const CREAM = "#f5ecd9";

/* Shared background — the same deep-night bed under every motif so
 * the six cards feel like variations of one universe. */
function NightBed({ id }) {
  return (
    <>
      <defs>
        <radialGradient id={`bed-${id}`} cx="50%" cy="30%" r="95%">
          <stop offset="0%" stopColor="#1a2648" />
          <stop offset="55%" stopColor="#0b1428" />
          <stop offset="100%" stopColor="#050a17" />
        </radialGradient>
      </defs>
      <rect x="0" y="0" width="400" height="260" fill={`url(#bed-${id})`} />
    </>
  );
}

/* Faint drifting specks — reused across motifs at low opacity to
 * suggest "the world behind the lights". */
function Specks({ seed = 1 }) {
  // Deterministic pseudo-random so every render matches the design.
  const rng = (n) => {
    const x = Math.sin(n * seed * 9.13) * 43758.5453;
    return x - Math.floor(x);
  };
  return (
    <g opacity="0.55">
      {Array.from({ length: 22 }).map((_, i) => {
        const cx = rng(i + 1) * 400;
        const cy = rng(i + 100) * 260;
        const r = 0.6 + rng(i + 200) * 0.7;
        const o = 0.2 + rng(i + 300) * 0.35;
        return <circle key={i} cx={cx} cy={cy} r={r} fill={GOLD} opacity={o} />;
      })}
    </g>
  );
}

const svgProps = {
  viewBox: "0 0 400 260",
  preserveAspectRatio: "xMidYMid slice",
  style: { display: "block", width: "100%", height: "100%" },
  "aria-hidden": true,
};

/* ── 01 · Reclaim ────────────────────────────────────────────────
 * "A place to return to yourself." A warm horizon-arc breaking
 * through the deep night — a personal sunrise. Warm amber halo. */
export function ReclaimMotif() {
  return (
    <svg {...svgProps}>
      <NightBed id="reclaim" />
      <defs>
        <radialGradient id="reclaim-halo" cx="50%" cy="88%" r="62%">
          <stop offset="0%" stopColor="rgba(240,180,120,0.7)" />
          <stop offset="40%" stopColor="rgba(224,169,122,0.28)" />
          <stop offset="100%" stopColor="rgba(224,169,122,0)" />
        </radialGradient>
        <linearGradient id="reclaim-horizon" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(224,169,122,0.0)" />
          <stop offset="100%" stopColor="rgba(240,180,120,0.55)" />
        </linearGradient>
      </defs>
      <Specks seed={2} />
      {/* Warm ground glow */}
      <ellipse cx="200" cy="230" rx="210" ry="90" fill="url(#reclaim-halo)" />
      {/* Rising sun disc */}
      <circle cx="200" cy="205" r="34" fill="url(#reclaim-horizon)" />
      <circle cx="200" cy="205" r="34" fill="none" stroke={GOLD} strokeWidth="0.7" opacity="0.7" />
      {/* Horizon line */}
      <line x1="20" y1="205" x2="380" y2="205" stroke={GOLD} strokeWidth="0.6" opacity="0.5" />
      {/* Faint aurora ribbons above */}
      {[0, 1, 2].map((i) => (
        <path
          key={i}
          d={`M 0 ${140 - i * 12} Q 200 ${110 - i * 14} 400 ${140 - i * 12}`}
          fill="none"
          stroke={GOLD}
          strokeWidth="0.4"
          opacity={0.28 - i * 0.06}
        >
          <animate
            attributeName="opacity"
            values={`${0.28 - i * 0.06};${0.4 - i * 0.08};${0.28 - i * 0.06}`}
            dur={`${9 + i * 2}s`}
            repeatCount="indefinite"
          />
        </path>
      ))}
    </svg>
  );
}

/* ── 02 · The Atelier ──────────────────────────────────────────
 * "Consciously re-architect the inner world." A precise geometric
 * mandala — thin gold rings rotating slowly. Architect's compass. */
export function AtelierMotif() {
  return (
    <svg {...svgProps}>
      <NightBed id="atelier" />
      <Specks seed={3} />
      <defs>
        <radialGradient id="atelier-glow" cx="50%" cy="50%" r="45%">
          <stop offset="0%" stopColor="rgba(224,169,122,0.35)" />
          <stop offset="60%" stopColor="rgba(224,169,122,0.08)" />
          <stop offset="100%" stopColor="rgba(224,169,122,0)" />
        </radialGradient>
      </defs>
      <circle cx="200" cy="130" r="110" fill="url(#atelier-glow)" />
      {/* Three thin concentric rings */}
      {[100, 78, 52].map((r, i) => (
        <circle
          key={r}
          cx="200"
          cy="130"
          r={r}
          fill="none"
          stroke={GOLD}
          strokeWidth={0.7}
          opacity={0.55 - i * 0.1}
          strokeDasharray={i === 1 ? "2 6" : "none"}
        />
      ))}
      {/* Rotating tick marks */}
      <g style={{ transformOrigin: "200px 130px", animation: "atelier-rot 40s linear infinite" }}>
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i * Math.PI) / 6;
          const x1 = 200 + Math.cos(a) * 100;
          const y1 = 130 + Math.sin(a) * 100;
          const x2 = 200 + Math.cos(a) * 108;
          const y2 = 130 + Math.sin(a) * 108;
          return (
            <line
              key={i}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={GOLD} strokeWidth={0.7} opacity={0.65}
            />
          );
        })}
      </g>
      {/* Cross-hair */}
      <line x1="200" y1="30" x2="200" y2="230" stroke={GOLD} strokeWidth="0.35" opacity="0.28" />
      <line x1="100" y1="130" x2="300" y2="130" stroke={GOLD} strokeWidth="0.35" opacity="0.28" />
      {/* Focal jewel */}
      <circle cx="200" cy="130" r="4.5" fill={CREAM} opacity="0.95" />
      <circle cx="200" cy="130" r="11" fill="none" stroke={CREAM} strokeWidth="0.4" opacity="0.35" />
      <style>{`@keyframes atelier-rot { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}

/* ── 03 · Soulumo ─────────────────────────────────────────────
 * "Personal installations from words, voice, music, frequency."
 * A soft audio-form: three symmetric sound bands radiating from
 * a warm centre — a voice becoming a signal. */
export function SoulumoMotif() {
  return (
    <svg {...svgProps}>
      <NightBed id="soulumo" />
      <Specks seed={4} />
      <defs>
        <radialGradient id="soulumo-glow" cx="50%" cy="50%" r="38%">
          <stop offset="0%" stopColor="rgba(255,208,180,0.55)" />
          <stop offset="50%" stopColor="rgba(224,169,122,0.15)" />
          <stop offset="100%" stopColor="rgba(224,169,122,0)" />
        </radialGradient>
        <linearGradient id="soulumo-wave" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="rgba(224,169,122,0)" />
          <stop offset="50%"  stopColor="rgba(240,180,120,0.85)" />
          <stop offset="100%" stopColor="rgba(224,169,122,0)" />
        </linearGradient>
      </defs>
      <circle cx="200" cy="130" r="100" fill="url(#soulumo-glow)" />
      {/* Three sound bands */}
      {[0, 1, 2].map((i) => (
        <path
          key={i}
          d={`M 30 130 Q 115 ${130 - 32 - i * 8} 200 130 T 370 130`}
          fill="none"
          stroke="url(#soulumo-wave)"
          strokeWidth={0.85}
          opacity={0.85 - i * 0.22}
        >
          <animate
            attributeName="d"
            values={`
              M 30 130 Q 115 ${130 - 32 - i * 8} 200 130 T 370 130;
              M 30 130 Q 115 ${130 + 32 + i * 8} 200 130 T 370 130;
              M 30 130 Q 115 ${130 - 32 - i * 8} 200 130 T 370 130
            `}
            dur={`${7 + i * 1.6}s`}
            repeatCount="indefinite"
          />
        </path>
      ))}
      {/* Central voice pearl */}
      <circle cx="200" cy="130" r="5" fill={CREAM} opacity="0.95">
        <animate attributeName="r" values="5;6.5;5" dur="4s" repeatCount="indefinite" />
      </circle>
      <circle cx="200" cy="130" r="14" fill="none" stroke={CREAM} strokeWidth="0.4" opacity="0.35" />
    </svg>
  );
}

/* ── 04 · The Absorption Engine ────────────────────────────────
 * "Knowledge become familiar." Concentric golden ripples radiating
 * from a still centre — a stone settling into deep water. */
export function AbsorptionMotif() {
  return (
    <svg {...svgProps}>
      <NightBed id="absorption" />
      <Specks seed={5} />
      <defs>
        <radialGradient id="absorption-glow" cx="50%" cy="52%" r="42%">
          <stop offset="0%" stopColor="rgba(224,169,122,0.55)" />
          <stop offset="35%" stopColor="rgba(224,169,122,0.18)" />
          <stop offset="100%" stopColor="rgba(224,169,122,0)" />
        </radialGradient>
        <filter id="absorption-blur">
          <feGaussianBlur stdDeviation="0.6" />
        </filter>
      </defs>
      <circle cx="200" cy="135" r="120" fill="url(#absorption-glow)" />
      {[22, 42, 66, 94, 126].map((r, i) => (
        <circle
          key={r}
          cx="200"
          cy="135"
          r={r}
          fill="none"
          stroke={GOLD}
          strokeWidth={0.7}
          opacity={0.75 - i * 0.13}
          filter="url(#absorption-blur)"
        >
          <animate
            attributeName="r"
            values={`${r};${r + 3};${r}`}
            dur={`${8 + i * 1.4}s`}
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values={`${0.75 - i * 0.13};${0.55 - i * 0.11};${0.75 - i * 0.13}`}
            dur={`${8 + i * 1.4}s`}
            repeatCount="indefinite"
          />
        </circle>
      ))}
      <circle cx="200" cy="135" r="4" fill={CREAM} opacity="0.92" />
      <circle cx="200" cy="135" r="9" fill="none" stroke={CREAM} strokeWidth="0.4" opacity="0.35" />
      <line x1="0" y1="220" x2="400" y2="220" stroke="rgba(224,169,122,0.34)" strokeWidth="0.5" opacity="0.5" />
    </svg>
  );
}

/* ── 05 · Soulumo Foundations ──────────────────────────────────
 * "A quiet, curated collection." Four horizontal strata — Morning
 * Light, Peace, Self-Belief, Rest — like layered dawn light, each
 * with a small glowing dot to suggest a single ritual anchor. */
export function FoundationsMotif() {
  return (
    <svg {...svgProps}>
      <NightBed id="foundations" />
      <Specks seed={6} />
      <defs>
        <linearGradient id="strat-1" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="rgba(255,220,190,0)" />
          <stop offset="45%" stopColor="rgba(255,220,190,0.55)" />
          <stop offset="100%" stopColor="rgba(255,220,190,0)" />
        </linearGradient>
        <linearGradient id="strat-2" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="rgba(224,169,122,0)" />
          <stop offset="55%" stopColor="rgba(224,169,122,0.5)" />
          <stop offset="100%" stopColor="rgba(224,169,122,0)" />
        </linearGradient>
        <linearGradient id="strat-3" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="rgba(245,236,217,0)" />
          <stop offset="50%" stopColor="rgba(245,236,217,0.4)" />
          <stop offset="100%" stopColor="rgba(245,236,217,0)" />
        </linearGradient>
        <linearGradient id="strat-4" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="rgba(180,150,210,0)" />
          <stop offset="50%" stopColor="rgba(180,150,210,0.35)" />
          <stop offset="100%" stopColor="rgba(180,150,210,0)" />
        </linearGradient>
      </defs>
      {/* Four stratum bands */}
      {[
        { y: 78,  grad: "strat-1", cx: 140 },  // Morning Light
        { y: 118, grad: "strat-2", cx: 250 },  // Peace
        { y: 158, grad: "strat-3", cx: 180 },  // Self-Belief
        { y: 198, grad: "strat-4", cx: 270 },  // Rest
      ].map((s, i) => (
        <g key={i}>
          <line
            x1="20" y1={s.y} x2="380" y2={s.y}
            stroke={`url(#${s.grad})`} strokeWidth="1.4"
          />
          <line
            x1="20" y1={s.y + 4} x2="380" y2={s.y + 4}
            stroke={`url(#${s.grad})`} strokeWidth="0.5" opacity="0.55"
          />
          <circle cx={s.cx} cy={s.y} r="2.5" fill={CREAM} opacity="0.92">
            <animate
              attributeName="opacity"
              values="0.92;0.55;0.92"
              dur={`${5 + i}s`}
              begin={`${i * 0.7}s`}
              repeatCount="indefinite"
            />
          </circle>
        </g>
      ))}
    </svg>
  );
}

/* ── 06 · The Daily Routine ────────────────────────────────────
 * "A simple daily rhythm." Six small orbs arranged in a circle —
 * gratitude, activation, intention, immersion, reset, reflection.
 * A slow rotation of thin connective arcs suggests one flowing day. */
export function DailyMotif() {
  const cx = 200, cy = 130, R = 78;
  const orbs = Array.from({ length: 6 }).map((_, i) => {
    const a = (i * Math.PI * 2) / 6 - Math.PI / 2;
    return { x: cx + Math.cos(a) * R, y: cy + Math.sin(a) * R, i };
  });
  return (
    <svg {...svgProps}>
      <NightBed id="daily" />
      <Specks seed={7} />
      <defs>
        <radialGradient id="daily-glow" cx="50%" cy="50%" r="40%">
          <stop offset="0%" stopColor="rgba(224,169,122,0.32)" />
          <stop offset="65%" stopColor="rgba(224,169,122,0.06)" />
          <stop offset="100%" stopColor="rgba(224,169,122,0)" />
        </radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r="95" fill="url(#daily-glow)" />
      {/* Connective ring — the rhythm */}
      <circle
        cx={cx} cy={cy} r={R}
        fill="none" stroke={GOLD} strokeWidth="0.6"
        opacity="0.6" strokeDasharray="3 5"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from={`0 ${cx} ${cy}`}
          to={`360 ${cx} ${cy}`}
          dur="40s"
          repeatCount="indefinite"
        />
      </circle>
      {/* Six ritual orbs */}
      {orbs.map((o) => (
        <g key={o.i}>
          <circle cx={o.x} cy={o.y} r="8" fill="rgba(224,169,122,0.14)" />
          <circle cx={o.x} cy={o.y} r="3.6" fill={CREAM} opacity="0.9">
            <animate
              attributeName="opacity"
              values="0.9;0.5;0.9"
              dur="6s"
              begin={`${o.i * 1.0}s`}
              repeatCount="indefinite"
            />
          </circle>
        </g>
      ))}
      {/* Centre — the person the day is for */}
      <circle cx={cx} cy={cy} r="4.5" fill={GOLD} opacity="0.85" />
      <circle cx={cx} cy={cy} r="10" fill="none" stroke={GOLD} strokeWidth="0.4" opacity="0.4" />
    </svg>
  );
}

/* Registry — lookup by doorway id used in ExploreVisionSection. */
export const MOTIF_BY_ID = {
  reclaim:     ReclaimMotif,
  atelier:     AtelierMotif,
  soulumo:     SoulumoMotif,
  absorption:  AbsorptionMotif,
  foundations: FoundationsMotif,
  daily:       DailyMotif,
};

export default MOTIF_BY_ID;
